import { useState } from "react";
import Utility from "../utils/utility";

// Shared rendering for a member's SMS/AI/admin/host message thread. Extracted from
// the Conversations view so the Arbitration view renders threads identically.

export const toMillis = (ts) => {
  const d = Utility.convertDate(ts);
  return d ? d.getTime() : 0;
};

export const formatRelativeTime = (ms) => {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ms).toLocaleDateString();
};

export const formatTimestamp = (ms) =>
  ms
    ? new Date(ms).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

// Maps a stored message `type` to who sent it and which side of the thread it sits on.
export const TYPE_META = {
  sms: { label: "Member", side: "in" },
  ai: { label: "AI", side: "out" },
  admin: { label: "Admin", side: "out" },
  host: { label: "Host", side: "out" },
};

export function MessageBubble({ msg }) {
  const [showContext, setShowContext] = useState(false);
  const meta = TYPE_META[msg.type] || {
    label: msg.type || "?",
    // Fall back to direction when the type is unknown.
    side: msg.direction === "inbound" ? "in" : "out",
  };
  const isIn = meta.side === "in";

  const hasContext =
    msg.hiddenContext != null &&
    !(typeof msg.hiddenContext === "object" &&
      Object.keys(msg.hiddenContext).length === 0);

  // The AI tried to forward the member's message to a human and failed
  // (expired/missing relay or Twilio error) - the reply above this never
  // reached anyone.
  const droppedForward =
    typeof msg.hiddenContext === "object" &&
    msg.hiddenContext?.forwarded === false;

  const contextText =
    typeof msg.hiddenContext === "object"
      ? JSON.stringify(msg.hiddenContext, null, 2)
      : String(msg.hiddenContext);

  return (
    <div className={`flex ${isIn ? "justify-start" : "justify-end"}`}>
      <div className={`flex flex-col ${isIn ? "items-start" : "items-end"} max-w-[75%]`}>
        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-0.5 px-1">
          {meta.label} · {formatTimestamp(msg._ms)}
        </span>
        <div
          className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
            isIn
              ? "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-sm"
              : "bg-primary text-white rounded-br-sm"
          }`}
        >
          {msg.content || <span className="opacity-60">(no text)</span>}
        </div>
        {droppedForward && (
          <span className="text-[10px] font-semibold text-red-600 dark:text-red-400 mt-0.5 px-1">
            ⚠ Member's message was never forwarded to a human
          </span>
        )}
        {hasContext && (
          <div className="mt-1 w-full">
            <button
              onClick={() => setShowContext((v) => !v)}
              className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 px-1"
            >
              {showContext ? "▾ hide context" : "▸ context"}
            </button>
            {showContext && (
              <pre className="mt-1 p-2 bg-gray-800 text-gray-100 text-[10px] rounded-lg overflow-x-auto max-w-full">
                {contextText}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Presentational scroll container for a thread. `className` overrides the default
// styling so callers can drop it into either a flex pane (Conversations) or a
// fixed-height card section (Arbitration).
export function MessageThread({
  messages,
  loading,
  containerRef,
  emptyText = "No messages.",
  className = "flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800 space-y-3",
}) {
  return (
    <div ref={containerRef} className={className}>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center text-gray-400 dark:text-gray-500 text-sm">{emptyText}</div>
      ) : (
        messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)
      )}
    </div>
  );
}
